import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';

import { AccountComponent } from './account.component';
import { AuthenticationGuard } from '@app/core/guards/authentication/authentication.guard';
import { PermissionGuard } from '@app/core/guards/permission/permission.guard';
import { Version2Guard } from '@app/core/guards/version-2/version2-guard.guard';
import { ExistingUserGuard } from '@app/core/guards/existing-user/existing-user.guard';
import { FourOhFourComponent } from './four-oh-four/four-oh-four.component';
import { ServerErrorComponent } from './server-error/server-error.component';
import { PermissionEnum } from '@app/generated/graphql';
import { ShareGuard } from '../core/guards/share/share.guard';
import { onboardingGuard } from '@app/core/guards/onboarding/onboarding.guard';

export const routes: Routes = [
  {
    path: 'account',
    component: AccountComponent,
    canActivate: [AuthenticationGuard],
    children: [
      // Used to showcase examples of components and funtionalities
      {
        path: 'component-examples',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesDemo,
        },
        loadChildren: () =>
          import('./examples/components/component-examples.module').then(
            (m) => m.ComponentExamplesModule,
          ),
      },
      // Used to showcase examples of features and functionalities
      {
        path: 'feature-examples',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesDemo,
        },
        loadChildren: () =>
          import('./examples/features/feature-examples.module').then(
            (m) => m.FeatureExamplesModule,
          ),
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        pathMatch: 'full',
        canActivate: [PermissionGuard, Version2Guard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesDashboard,
        },
        loadChildren: () =>
          import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
      },
      {
        path: 'settings',
        pathMatch: 'full',
        canActivate: [PermissionGuard],
        data: {
          resourceId: PermissionEnum.PagesSettings,
        },
        loadChildren: () =>
          import('./settings/settings.module').then((m) => m.SettingsModule),
      },
      {
        path: 'company',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesCompany,
        },
        loadChildren: () =>
          import('./company/company.module').then((m) => m.CompanyModule),
      },
      {
        path: 'order/:id',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesViewOrder,
        },
        loadChildren: () =>
          import('./view-order/view-order.module').then(
            (m) => m.ViewOrderModule,
          ),
      },
      {
        path: 'orders',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesOrders,
        },
        loadChildren: () =>
          import('./orders/orders.module').then((m) => m.OrdersModule),
      },
      {
        path: 'offers',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesDemo,
        },
        loadChildren: () =>
          import('./offers/offers.module').then((m) => m.OffersModule),
      },
      {
        path: 'partners',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesPartners,
        },
        loadChildren: () =>
          import('./trade-partners/trade-partners.module').then(
            (m) => m.TradePartnersModule,
          ),
      },
      {
        path: 'estimations',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesEstimations,
        },
        loadChildren: () =>
          import('./estimation/estimation.module').then(
            (m) => m.EstimationModule,
          ),
      },
      {
        path: 'planning',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesPlanning,
        },
        loadChildren: () =>
          import('./planning/planning.module').then((m) => m.PlanningModule),
      },
      {
        path: 'pack-instructions',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesPackInstructions,
        },
        loadChildren: () =>
          import('./pack-instructions/pack-instructions.module').then(
            (m) => m.PackInstructionsModule,
          ),
      },
      {
        path: 'load-out-instructions',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesLoadOutInstructions,
        },
        loadChildren: () =>
          import('./load-out-instructions/load-out-instructions.module').then(
            (m) => m.LoadOutInstructionsModule,
          ),
      },
      {
        path: 'shipments',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesShipments,
        },
        loadChildren: () =>
          import('./shipments/shipments.module').then((m) => m.ShipmentsModule),
      },
      {
        path: 'air-freight-shipments',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesAirFreightShipments,
        },
        loadChildren: () =>
          import('./air-freight-shipments/air-freight-shipments.module').then(
            (m) => m.AirFreightShipmentsModule,
          ),
      },
      {
        path: 'arrival-qc',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesClaims,
        },
        loadChildren: () =>
          import('./quality-and-compliance/claims.module').then(
            (m) => m.ClaimsModule,
          ),
      },
      {
        path: 'commercial-invoices',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesCommercialInvoices,
        },
        loadChildren: () =>
          import('./commercial-invoices/commercial-invoices.module').then(
            (m) => m.CommercialInvoicesModule,
          ),
      },
      {
        path: 'cash-flow',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          isLineItemBased: true,
          resourceId: PermissionEnum.PagesCashFlow,
        },
        loadChildren: () =>
          import('./cash-flow/cash-flow.module').then((m) => m.CashFlowModule),
      },
      {
        path: 'finance',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesCashFlow,
        },
        loadChildren: () =>
          import('./finance/finance.module').then((m) => m.FinanceModule),
      },
      {
        path: 'commercial-terms',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesCommercialTerms,
        },
        loadChildren: () =>
          import('./commercial-terms/commercial-terms.module').then(
            (m) => m.CommercialTermsModule,
          ),
      },
      {
        path: 'financial-invoices',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesCustomerInvoices,
        },
        loadChildren: () =>
          import('./financial-invoices/financial-invoices.module').then(
            (m) => m.FinancialInvoicesModule,
          ),
      },
      {
        path: 'vendor-invoices',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesVendorInvoices,
        },
        loadChildren: () =>
          import('./vendor-invoices/vendor-invoices.module').then(
            (m) => m.VendorInvoicesModule,
          ),
      },
      {
        path: 'default-costs',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesDefaultCosts,
        },
        loadChildren: () =>
          import('./default-costs/default-costs.module').then(
            (m) => m.DefaultCostsModule,
          ),
      },
      {
        path: 'finance-rules',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesFinanceRuleManagement,
        },
        loadChildren: () =>
          import('./finance-rules/finance-rules.module').then(
            (m) => m.FinanceRulesModule,
          ),
      },
      {
        path: 'payments',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesPayments,
        },
        loadChildren: () =>
          import('./payments/payments.module').then((m) => m.PaymentsModule),
      },
      {
        path: 'data-exports',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesDataExports,
        },
        loadChildren: () =>
          import('./data-exports/data-exports.module').then(
            (m) => m.DataExportsModule,
          ),
      },
      {
        path: 'insights',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesInsights,
        },
        loadChildren: () =>
          import('./insights/index/index.module').then(
            (m) => m.InsightsIndexModule,
          ),
      },
      {
        path: 'incoterm-calculator',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesIncotermCalculator,
        },
        loadChildren: () =>
          import('./net-returns-calculator/net-returns-calculator.module').then(
            (m) => m.NetReturnsCalculatorModule,
          ),
      },
      {
        path: 'insights/report-group/:id',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesInsights,
        },
        loadChildren: () =>
          import('./insights/view-report-group/view.module').then(
            (m) => m.InsightsReportGroupViewModule,
          ),
      },
      {
        path: 'insights/:id',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesInsights,
        },
        loadChildren: () =>
          import('./insights/view-metabase/view.module').then(
            (m) => m.InsightsMetabaseViewModule,
          ),
      },
      {
        path: 'stock',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesStock,
        },
        loadChildren: () =>
          import('./stock/stock.module').then((m) => m.StockModule),
      },
      {
        path: 'season-setup',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard, onboardingGuard],
        data: {
          resourceId: PermissionEnum.PagesMasterfiles,
        },
        loadChildren: () =>
          import('./season-setup/season-setup.module').then(
            (m) => m.SeasonSetupModule,
          ),
      },
      {
        path: 'notification-preferences',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesSettings,
        },
        loadChildren: () =>
          import(
            './notification-preferences/notification-preferences.module'
          ).then((m) => m.NotificationPreferencesModule),
      },
      {
        path: 'packhouse-confirmation',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.MasterfilesPackhouses,
        },
        loadChildren: () =>
          import(
            './packhouse-confirmation-dashboard/packhouse-confirmation-dashboard.module'
          ).then((m) => m.PackhouseConfirmationDashboardModule),
      },
      {
        path: 'data-confirmation-dashboard',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesMasterfiles,
        },
        loadChildren: () =>
          import(
            './data-confirmation-dashboard/data-confirmation-dashboard.module'
          ).then((m) => m.DataConfirmationDashboardModule),
      },
      {
        path: 'supply-chain-confirmation',
        pathMatch: 'full',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesMasterfiles,
        },
        loadChildren: () =>
          import(
            './supply-chain-confirmation/supply-chain-confirmation.module'
          ).then((m) => m.SupplyChainConfirmationModule),
      },
      {
        path: 'masterfiles/farm-varieties',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.MasterfilesFarmVarieties,
        },
        loadChildren: () =>
          import('./masterfiles/farm-varieties/farm-varieties.module').then(
            (m) => m.FarmVarietiesModule,
          ),
      },
      {
        path: 'masterfiles/orchards',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.MasterfilesOrchards,
        },
        loadChildren: () =>
          import('./masterfiles/orchards/orchards.module').then(
            (m) => m.OrchardsModule,
          ),
      },
      {
        path: 'masterfiles/food-business-operators',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.MasterfilesFoodBusinessOperators,
        },
        loadChildren: () =>
          import(
            './masterfiles/food-business-operators/food-business-operators.module'
          ).then((m) => m.FoodBusinessOperatorsModule),
      },
      {
        path: 'masterfiles/carton-conversions',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.MasterfilesCartonConversions,
        },
        loadChildren: () =>
          import(
            './masterfiles/carton-conversions/carton-conversions.module'
          ).then((m) => m.CartonConversionsModule),
      },
      {
        path: 'masterfiles/errors',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.MasterfilesErrors,
        },
        loadChildren: () =>
          import('./masterfiles/errors/errors.module').then(
            (m) => m.ErrorsModule,
          ),
      },
      {
        path: 'masterfiles/packhouse-manager',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.MasterfilesPackhouses,
        },
        loadChildren: () =>
          import(
            './masterfiles/packhouse-manager/packhouse-manager.module'
          ).then((m) => m.PackhouseManagerModule),
      },
      {
        path: 'masterfiles/supply-chain-setup',
        canActivate: [PermissionGuard, ExistingUserGuard],
        data: {
          resourceId: PermissionEnum.PagesMasterfiles,
        },
        loadChildren: () =>
          import(
            './masterfiles/supply-chain-setup/supply-chain-setup.module'
          ).then((m) => m.SupplyChainSetupModule),
      },
      {
        path: '404',
        component: FourOhFourComponent,
        data: {},
      },
      {
        path: '500',
        component: ServerErrorComponent,
        data: {},
      },
      {
        path: '**',
        redirectTo: '/account/404',
        data: {},
      },
    ],
  },
  {
    path: 'shared/insights/report-group/:id',
    pathMatch: 'full',
    canActivate: [ShareGuard],
    loadChildren: () =>
      import('./insights/view-report-group/view.module').then(
        (m) => m.InsightsReportGroupViewModule,
      ),
  },
  {
    path: 'shared/insights/report-group-cycle/:id',
    pathMatch: 'full',
    canActivate: [ShareGuard],
    loadChildren: () =>
      import('./insights/report-group-cycle/report-group-cycle.module').then(
        (m) => m.ReportGroupCycleModule,
      ),
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class AccountRoutingModule {}
